import { useEffect } from 'react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { reportAgentEvent } from '@common/services/agent-analytics';

export type AgentPageSurface = 'web_agents_page' | 'extension_inchat';

/**
 * Fire ONE durable `agent_page_viewed` (platform 'claude') when a REAL agents
 * surface opens, with the authed owner. Two surfaces:
 *  - 'web_agents_page'  — the web /agents route (MyBridgePage) mounting.
 *  - 'extension_inchat' — the in-chat Quick Agents panel (QuickAgentsSidebar) mounting.
 *
 * Why mount, not a click: the audit's #1 gap is that distinct owners opening
 * /agents is invisible and `owner_project_created` is polluted — 876/893
 * `/my-bridge/overview` calls are the extension's PASSIVE poll (chat-agent-button
 * keeps the overview query warm whether or not the panel is open), not a real
 * open. This hook lives ONLY in components that mount on a genuine user open
 * (the web route component, and the conditionally-mounted Quick Agents panel —
 * every opener of which is a user action), so it NEVER fires on the passive poll.
 * A re-open remounts the component and fires again; a re-render does not.
 */
export function useAgentPageViewed(surface: AgentPageSurface): void {
  const owner = useAppSelector((s) => s.UserReducer?.userId);
  useEffect(() => {
    reportAgentEvent('agent_page_viewed', 'claude', { surface, owner });
    // Fire once per mount; surface/owner captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
